import {
  DatepickerSimple,
  Select,
  Timestamp,
  BankFileConversionRail,
  FileConversionConfiguration,
  objectEntries,
  InteracWordmark,
  FileConversionService,
  FieldData,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { ReactNode } from 'react';

export type DownloadFileFormValues = {
  Service: FileConversionService;
  PaymentRail: BankFileConversionRail | '';
  FileConversionConfigurationID: string;
  ValueDate: string;
  FallbackPaymentRail: BankFileConversionRail | '';
};

export const useDownloadFileFormFields = ({
  configurations,
  configurationsLoading,
}: {
  configurations: FileConversionConfiguration[];
  configurationsLoading: boolean;
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.FileUploadDrawer.ConvertTemplate' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const railNames: Record<BankFileConversionRail, ReactNode> = {
    eft: tGlobal('PaymentRail.eft'),
    vopayinstant: tGlobal('PaymentRail.vopayinstant'),
    ach: tGlobal('PaymentRail.ach'),
    rtp: tGlobal('PaymentRail.rtp'),
    fednow: tGlobal('PaymentRail.fednow'),
    fedwire: tGlobal('PaymentRail.wire'),
    interac: <InteracWordmark />,
    aft: tGlobal('PaymentRail.aft'),
    creditcard: tGlobal('PaymentRail.creditcard'),
    debitcard: tGlobal('PaymentRail.debitcard'),
    oct: tGlobal('PaymentRail.oct'),
  };

  const getValidServiceKey = (service: string) => {
    const serviceKey = service.toLowerCase();
    return serviceKey === 'credit' || serviceKey === 'debit' ? serviceKey : undefined;
  };

  const getBankOptions = (service: string) => {
    const validServiceKey = getValidServiceKey(service);

    return configurations
      .filter((config) => {
        if (!validServiceKey) return false;
        const railsObj = config.PaymentRails?.[validServiceKey];
        return railsObj && typeof railsObj === 'object' && Object.keys(railsObj).length > 0;
      })
      .map((config) => ({
        value: config.FileConversionConfigurationID,
        label: config.AccountNumber
          ? `${config.FinancialInstitution} - ${config.AccountNumber}`
          : config.FinancialInstitution,
      }));
  };

  const getProductOptions = (conversionID: string, service: string) => {
    const bankConfig = configurations.find((config) => config.FileConversionConfigurationID === conversionID);
    if (!bankConfig) return [];

    const validServiceKey = getValidServiceKey(service);
    const railsObj = validServiceKey ? bankConfig.PaymentRails?.[validServiceKey] : undefined;
    if (!railsObj || typeof railsObj !== 'object') return [];

    return objectEntries(railsObj).map(([rail, value]) => ({
      isDisabled: !value.DownloadEnabled,
      value: rail,
      label: railNames[rail],
    }));
  };

  const convertTo: FieldData<DownloadFileFormValues>[] = [
    {
      fieldName: 'FileConversionConfigurationID',
      label: () => t('processorLabel', { defaultValue: 'Processor' }),
      getInputState: () => 'required',
      showField: () => {
        // Hide if only default processor is available
        if (configurations.length === 1 && configurations[0].Processor === 'default_processor') {
          return false;
        }
        return true;
      },
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        const options = getBankOptions(ctx.values.Service);
        const formikData = getFormikData<DownloadFileFormValues['FileConversionConfigurationID']>({
          onChange: (event) => {
            const selectedValue = event.target?.value || '';
            const bankConfig = configurations.find((config) => config.FileConversionConfigurationID === selectedValue);

            if (!bankConfig) {
              ctx.setFieldValue('PaymentRail', '');
              ctx.setFieldTouched('PaymentRail', false);
              return;
            }

            const validServiceKey = getValidServiceKey(ctx.values.Service);
            const railsObj = validServiceKey ? bankConfig.PaymentRails?.[validServiceKey] : undefined;

            if (!railsObj || typeof railsObj !== 'object') {
              ctx.setFieldValue('PaymentRail', '');
              ctx.setFieldTouched('PaymentRail', false);
              return;
            }

            const defaultRail = objectEntries(railsObj).find(
              ([_, config]) => config.IsDefault && config.DownloadEnabled,
            );

            ctx.setFieldValue('PaymentRail', defaultRail ? defaultRail[0] : '');
            ctx.setFieldTouched('PaymentRail', false);
          },
        });

        return (
          <Select
            version="2"
            {...formikData}
            options={options}
            placeholder=""
            sort={false}
            loading={configurationsLoading}
          />
        );
      },
    },
    {
      fieldName: 'PaymentRail',
      label: () => t('productLabel', { defaultValue: 'Payment Rail' }),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        const productOption = getProductOptions(ctx.values.FileConversionConfigurationID, ctx.values.Service);
        return (
          <Select
            version="2"
            {...getFormikData<DownloadFileFormValues['PaymentRail']>({})}
            options={productOption}
            loading={configurationsLoading}
            placeholder=""
            disableOptionWhen={(o) => o.isDisabled}
            disabled={productOption.length === 0}
            sort={false}
          />
        );
      },
    },
    {
      fieldName: 'FallbackPaymentRail' as keyof DownloadFileFormValues,
      label: () => t('alternativePaymentRailLabel', { defaultValue: 'Fallback Payment Rail' }),
      getInputState: () => 'optional',
      showField: () => false,
      renderField: ({ getFormikData, ctx }) => {
        const productOption = getProductOptions(ctx.values.FileConversionConfigurationID, ctx.values.Service);

        return (
          <Select
            version="2"
            {...getFormikData<DownloadFileFormValues['FallbackPaymentRail']>({})}
            options={productOption}
            placeholder=""
            disableOptionWhen={(o) => o.isDisabled}
            disabled={productOption.length === 0}
            sort={false}
            loading={configurationsLoading}
          />
        );
      },
    },
    {
      fieldName: 'ValueDate',
      label: () => t('valueDateLabel', { defaultValue: 'Value Date' }),
      getInputState: () => 'required',
      serializeField: (values) => {
        const endDate = new Date(Number(values.ValueDate));
        return format(endDate, 'yyyy-MM-dd');
      },
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData }) => {
        const data = getFormikData<DownloadFileFormValues['ValueDate']>({});
        return (
          <DatepickerSimple
            variant="outlineShadow"
            position="topLeft"
            {...data}
            min={new Date().getTime() as Timestamp}
            isDisabled={data.disabled}
          />
        );
      },
    },
  ];

  return { convertTo };
};
